import * as React from 'react';
import {
    Checkbox,
    Dropdown,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    Stack,
    Text,
} from '@fluentui/react';
import type {
    AgentTaskProposalVote,
    AgentTaskProposedChangeResult,
    AgentTaskQuestion,
    AgentTaskQuestionSourceLocation,
} from '../types';
import { AgentTaskProposalVote as ProposalVote } from '../types';
import { formatNumber } from '../helpers';

export function calculateExpectedSnapshotReduction(
    taskReductionPercent?: number | null,
    taskSnapshotInclusivePercent?: number | null,
) {
    if (taskReductionPercent === undefined
        || taskReductionPercent === null
        || taskSnapshotInclusivePercent === undefined
        || taskSnapshotInclusivePercent === null) {
        return undefined;
    }

    return taskReductionPercent * taskSnapshotInclusivePercent / 100;
}

export function formatReduction(value?: number | null) {
    return value === undefined || value === null ? 'Not estimated' : `${formatNumber(value)}%`;
}

function formatLocation(location: AgentTaskQuestionSourceLocation, index: number) {
    const lines = location.startLine === undefined
        ? undefined
        : location.endLine !== undefined && location.endLine !== location.startLine
            ? `lines ${location.startLine}–${location.endLine}`
            : `line ${location.startLine}`;
    return [
        location.repository,
        location.project,
        location.path,
        location.symbol,
        lines,
    ].filter(Boolean).join(' • ') || `Source ${index + 1}`;
}

const OUTCOME_STYLES = {
    proposed: { background: '#deecf9', color: '#004578' },
    retained: { background: '#dff6dd', color: '#0b6a0b' },
    revised: { background: '#fff4ce', color: '#8a5700' },
    discarded: { background: '#edebe9', color: '#605e5c' },
};

const VOTE_OPTIONS: IDropdownOption[] = [
    { key: 'unvoted', text: 'Unvoted' },
    { key: ProposalVote.Ok, text: 'Ok' },
    { key: ProposalVote.Blocked, text: 'Blocked' },
    { key: ProposalVote.TooSmall, text: 'TooSmall' },
    { key: ProposalVote.Deferred, text: 'Deferred' },
];

const VOTE_STYLES: Record<string, { background: string; border: string; color: string }> = {
    unvoted: { background: '#f3f2f1', border: '#a19f9d', color: '#323130' },
    [ProposalVote.Ok]: { background: '#dff6dd', border: '#107c10', color: '#0b6a0b' },
    [ProposalVote.Blocked]: { background: '#fde7e9', border: '#d13438', color: '#a4262c' },
    [ProposalVote.TooSmall]: { background: '#edebe9', border: '#8a8886', color: '#605e5c' },
    [ProposalVote.Deferred]: { background: '#fff4ce', border: '#d29200', color: '#8a5700' },
};

function getVoteLabel(vote: AgentTaskProposalVote | null) {
    return VOTE_OPTIONS.find(option => option.key === (vote ?? 'unvoted'))?.text ?? 'Unvoted';
}

export function CandidateChangeCard({
    change,
    questions,
    taskSnapshotInclusivePercent,
    onVoteChange,
    voteUpdating,
    selectionEnabled,
    selected,
    onSelectionChange,
}: {
    change: AgentTaskProposedChangeResult;
    questions: AgentTaskQuestion[];
    taskSnapshotInclusivePercent?: number | null;
    onVoteChange?: (change: AgentTaskProposedChangeResult, vote: AgentTaskProposalVote | null) => void;
    voteUpdating?: boolean;
    selectionEnabled?: boolean;
    selected?: boolean;
    onSelectionChange?: (change: AgentTaskProposedChangeResult, selected: boolean) => void;
}) {
    const linkedQuestions = questions.filter(question =>
        (question.candidateChangeId ?? question.context?.candidateChange?.id) === change.candidateId,
    );
    const discarded = change.outcome === 'discarded';
    const snapshotImpact = calculateExpectedSnapshotReduction(
        change.expectedReductionPercent,
        taskSnapshotInclusivePercent,
    );
    const voteKey = change.vote ?? 'unvoted';
    const voteStyle = VOTE_STYLES[voteKey];
    const canEditVote = change.isCurrent && !discarded && !!onVoteChange;
    const canSelect = !!selectionEnabled
        && change.isCurrent
        && !discarded
        && change.vote === ProposalVote.Ok;

    return (
        <article
            aria-label={`${discarded ? 'Discarded' : 'Active'} candidate change ${change.candidateId}`}
            style={{
                border: `1px solid ${discarded ? '#c8c6c4' : '#8a8886'}`,
                borderLeft: `5px solid ${discarded ? '#a19f9d' : '#0078d4'}`,
                borderRadius: 6,
                padding: 12,
                background: discarded ? '#faf9f8' : '#ffffff',
                opacity: discarded ? 0.82 : 1,
            }}
        >
            <Stack horizontal wrap verticalAlign="center" tokens={{ childrenGap: 8 }}>
                <Text variant="mediumPlus"><strong>{change.candidateId || 'Unidentified change'}</strong></Text>
                <span
                    style={{
                        ...OUTCOME_STYLES[change.outcome],
                        borderRadius: 12,
                        padding: '2px 9px',
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'capitalize',
                    }}
                >
                    {change.outcome}
                </span>
            </Stack>
            <div style={{ marginTop: 8 }}><strong>{change.summary || change.text}</strong></div>
            {change.rationale && (
                <div style={{ marginTop: 6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    <strong>Rationale:</strong> {change.rationale}
                </div>
            )}
            <div style={{ marginTop: 6 }}>
                <strong>Expected task reduction:</strong> {formatReduction(change.expectedReductionPercent)}
            </div>
            <div style={{ marginTop: 4 }}>
                <strong>Expected snapshot reduction:</strong> {formatReduction(snapshotImpact)}
            </div>
            {change.locations.length > 0 && (
                <div style={{ marginTop: 10 }}>
                    <strong>Verified sources</strong>
                    <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                        {change.locations.map((location, index) => (
                            <li key={`${location.codeUrl ?? location.path ?? 'source'}:${location.startLine ?? ''}:${index}`}>
                                {location.codeUrl ? (
                                    <a href={location.codeUrl} target="_blank" rel="noopener noreferrer">
                                        {formatLocation(location, index)}
                                    </a>
                                ) : formatLocation(location, index)}
                                {location.ref && <div><Text variant="small">Ref: {location.ref}</Text></div>}
                                {location.snippet && (
                                    <details style={{ margin: '4px 0 8px' }}>
                                        <summary style={{ cursor: 'pointer' }}>Show safe snippet</summary>
                                        <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', overflowX: 'auto', margin: '6px 0 0', padding: 8, background: '#f3f2f1' }}>
                                            <code>{location.snippet}</code>
                                        </pre>
                                    </details>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {linkedQuestions.length > 0 && (
                <aside
                    aria-label={`Clarifications for ${change.candidateId}`}
                    style={{ marginTop: 10, padding: 10, background: discarded ? '#f3f2f1' : '#f3f8fc', borderRadius: 4 }}
                >
                    <strong>Linked clarifications</strong>
                    {linkedQuestions.map(question => (
                        <div key={question.questionId} style={{ marginTop: 6 }}>
                            <span>Q: {question.question}</span>
                            <div>
                                <Text variant="small">
                                    {question.declined
                                        ? 'Answer: Declined'
                                        : question.answer?.trim()
                                            ? `Answer: ${question.answer}`
                                            : 'Awaiting answer'}
                                </Text>
                            </div>
                        </div>
                    ))}
                </aside>
            )}
            <div
                style={{
                    marginTop: 12,
                    padding: 10,
                    border: `1px solid ${voteStyle.border}`,
                    borderRadius: 4,
                    background: voteStyle.background,
                    color: voteStyle.color,
                }}
            >
                <Stack horizontal wrap verticalAlign="end" tokens={{ childrenGap: 12 }}>
                    {canEditVote ? (
                        <div style={{ minWidth: 180 }}>
                            <Dropdown
                                label="Vote"
                                ariaLabel={`Vote for ${change.candidateId || 'candidate change'}`}
                                options={VOTE_OPTIONS}
                                selectedKey={voteKey}
                                disabled={voteUpdating}
                                onChange={(_, option) => {
                                    const nextVote = option?.key === 'unvoted'
                                        ? null
                                        : Number(option?.key) as AgentTaskProposalVote;
                                    onVoteChange(change, nextVote);
                                }}
                            />
                        </div>
                    ) : (
                        <div>
                            <Text variant="small">Vote</Text>
                            <div><strong>{getVoteLabel(change.vote)}</strong></div>
                        </div>
                    )}
                    {selectionEnabled && (
                        <Checkbox
                            label="Include in enqueue selection"
                            ariaLabel={`Include ${change.candidateId || 'candidate change'} in enqueue selection`}
                            checked={!!selected}
                            disabled={!canSelect || voteUpdating}
                            onChange={(_, checked) => onSelectionChange?.(change, checked ?? false)}
                        />
                    )}
                </Stack>
            </div>
        </article>
    );
}

export function ProposedChangesImpactSummary({
    expectedReductionPercent,
    taskSnapshotInclusivePercent,
    expectedSnapshotReductionPercent,
}: {
    expectedReductionPercent?: number | null;
    taskSnapshotInclusivePercent?: number | null;
    expectedSnapshotReductionPercent?: number | null;
}) {
    const snapshotImpact = expectedSnapshotReductionPercent
        ?? calculateExpectedSnapshotReduction(expectedReductionPercent, taskSnapshotInclusivePercent);

    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8 }}>
            <div><strong>Task share of snapshot:</strong> {formatReduction(taskSnapshotInclusivePercent)}</div>
            <div><strong>Expected task reduction:</strong> {formatReduction(expectedReductionPercent)}</div>
            <div><strong>Expected snapshot reduction:</strong> {formatReduction(snapshotImpact)}</div>
        </div>
    );
}

export function StructuredProposedChanges({
    resultKey,
    resultSummary,
    assumptions,
    noChangeReason,
    expectedReductionPercent,
    taskSnapshotInclusivePercent,
    expectedSnapshotReductionPercent,
    proposedChangeEvents,
    questions,
    onVoteChange,
    updatingVoteIds,
    selectionEnabled,
    selectedProposalIds,
    onSelectionChange,
}: {
    resultKey: string | number;
    resultSummary?: string | null;
    assumptions: string[];
    noChangeReason?: string | null;
    expectedReductionPercent?: number | null;
    taskSnapshotInclusivePercent?: number | null;
    expectedSnapshotReductionPercent?: number | null;
    proposedChangeEvents: AgentTaskProposedChangeResult[];
    questions: AgentTaskQuestion[];
    onVoteChange?: (change: AgentTaskProposedChangeResult, vote: AgentTaskProposalVote | null) => void;
    updatingVoteIds?: ReadonlySet<number>;
    selectionEnabled?: boolean;
    selectedProposalIds?: ReadonlySet<number>;
    onSelectionChange?: (change: AgentTaskProposedChangeResult, selected: boolean) => void;
}) {
    const active = proposedChangeEvents.filter(change => change.outcome !== 'discarded');
    const discarded = proposedChangeEvents.filter(change => change.outcome === 'discarded');

    return (
        <section aria-labelledby={`proposed-changes-${resultKey}`}>
            <Text id={`proposed-changes-${resultKey}`} variant="mediumPlus">Proposed changes</Text>
            {resultSummary && <p><strong>Summary:</strong> {resultSummary}</p>}
            <ProposedChangesImpactSummary
                expectedReductionPercent={expectedReductionPercent}
                taskSnapshotInclusivePercent={taskSnapshotInclusivePercent}
                expectedSnapshotReductionPercent={expectedSnapshotReductionPercent}
            />
            <p style={{ color: '#605e5c' }}>
                Candidate estimates can overlap; the result-set total is the authoritative non-overlapping estimate.
            </p>
            {assumptions.length > 0 && (
                <div>
                    <strong>Assumptions</strong>
                    <ul>{assumptions.map((assumption, index) => <li key={index}>{assumption}</li>)}</ul>
                </div>
            )}
            {noChangeReason && <p><strong>No-change reason:</strong> {noChangeReason}</p>}
            {active.length > 0 ? (
                <Stack tokens={{ childrenGap: 10 }}>
                    {active.map(change => (
                        <CandidateChangeCard
                            key={`${change.analysisItemId}:${change.candidateId}`}
                            change={change}
                            questions={questions}
                            taskSnapshotInclusivePercent={taskSnapshotInclusivePercent}
                            onVoteChange={onVoteChange}
                            voteUpdating={updatingVoteIds?.has(change.analysisItemId)}
                            selectionEnabled={selectionEnabled}
                            selected={selectedProposalIds?.has(change.analysisItemId)}
                            onSelectionChange={onSelectionChange}
                        />
                    ))}
                </Stack>
            ) : (
                <MessageBar messageBarType={MessageBarType.info}>No active candidate changes were recorded.</MessageBar>
            )}
            {discarded.length > 0 && (
                <section aria-label="Discarded change history" style={{ marginTop: 16 }}>
                    <Text variant="mediumPlus">Discarded change history</Text>
                    <p style={{ color: '#605e5c' }}>Historical candidates below are not part of the active proposal.</p>
                    <Stack tokens={{ childrenGap: 10 }}>
                        {discarded.map(change => (
                            <CandidateChangeCard
                                key={`${change.analysisItemId}:${change.candidateId}`}
                                change={change}
                                questions={questions}
                                taskSnapshotInclusivePercent={taskSnapshotInclusivePercent}
                                onVoteChange={onVoteChange}
                                voteUpdating={updatingVoteIds?.has(change.analysisItemId)}
                                selectionEnabled={selectionEnabled}
                                selected={selectedProposalIds?.has(change.analysisItemId)}
                                onSelectionChange={onSelectionChange}
                            />
                        ))}
                    </Stack>
                </section>
            )}
        </section>
    );
}
